import { Component, Input, OnInit, Output } from '@angular/core';
import { MessageService, SelectItem } from 'primeng/api';
import { IState } from '../../lookups';
import { ReplaySubject } from 'rxjs';
import {
    Advisor,
    CompanyInfo,
    Purchase
} from '../../customer-admin/customer-admin.model';
import moment from 'moment';
import { IUserInfo } from '../../user-info';
import { eFAACTCustomer } from '../store/efaact-customer/efaact-customer.model';
import { Job } from '../store/jobs/jobs.model';
import { QueueJob } from '../store/queue-job/queue-job.model';
import { TraceLoggingOverride } from '../store/trace-logging-override/trace-logging-override.model';
import * as fromAuth from '../../auth';
import { AuthorizenetCheckoutService } from '../../authorizenet-checkout/authorizenet-checkout.service';
@Component({
    selector: 'ef-internal-admin-presenter',
    templateUrl: './internal-admin-presenter.component.html',
    providers: [MessageService],
    styleUrls: ['./internal-admin-presenter.component.scss'],
    standalone: false
})
export class InternalAdminPresenterComponent implements OnInit {
    private ItemsSubject = new ReplaySubject<Array<CompanyInfo>>(1);
    private _items: Array<CompanyInfo>;
    @Input() set items(value: Array<CompanyInfo>) {
        if (value && value.length > 0) {
            this._items = value;
            this.ItemsSubject.next(value);
            if (value[0] != null) {
                const incomingCustomerId = value[0].CustomerID;
                const customerChanged =
                    String(incomingCustomerId ?? '') !== String(this.customerID ?? '');
                this.customerID = incomingCustomerId;
                this.customerPIN = value[0].PIN;
                this.productCode = value[0].ProductCode;
                this.adminCount = value[0].AdminCount;
                const incomingProfileId = value[0].CustomerProfileID;
                const incomingProfileText =
                    incomingProfileId != null ? String(incomingProfileId).trim() : '';
                if (customerChanged || incomingProfileText) {
                    this.customerProfileID = incomingProfileText;
                }
                this.companyName = value[0].Name;
                if (customerChanged || !this.editingCustomer) {
                    this.primaryemail = value[0].primaryemail;
                    this.savedPrimaryEmail = value[0].primaryemail ?? '';
                }
                this.address = value[0].Address1;
                this.address2 = value[0].Address2;
                this.address3 = value[0].Address3;
                this.city = value[0].City;
                this.selectedState = value[0].State;
                this.zip = value[0].ZipCode;
                this.installLink = value[0].efdinstallurl;
                this.selectedAdvisor = value[0].AdvisorID;
                this.modifiedBy = value[0].ModifiedBy;
                this.dbname = value[0].dbname;
                this.serverID = value[0].ServerID;
                this.serverName = value[0].ServerName;
                this.agentID = value[0].AgentID;
                this.crystalReportsLink = value[0].CRinstallurl;
                this.microsoftDriverLink = value[0].MSOLEDBinstallurl;
                this.isGCP = value[0].IsGCP;
                this.isQBO = value[0].IsQBO;
                this.taxID = value[0].TaxpayerID;
                this.hostName = value[0].HostName;
                this.hostProvider = value[0].HostProviderID;

                this.modified =
                    (value[0].ModifiedBy != null && value[0].ModifiedDate != null)
                        ? `Last modified on ${moment(
                              value[0].ModifiedDate
                          ).format('MM/DD/YYYY HH:mm:ss')} by ${
                              value[0].ModifiedBy
                          }`
                        : 'No previous updates found.';

                value[0].Purchases.forEach(x => {
                    x.PaidThruDate = new Date(x.MaintPaidThruDate);
                });
            }
        }
    }
    get items(): Array<CompanyInfo> {
        return this._items;
    }

    private StatesSubject = new ReplaySubject<Array<IState>>(1);
    private _states: Array<IState>;
    @Input() set states(value: Array<IState>) {
        if (value && value.length > 0) {
            this._states = value;
            this.StatesSubject.next(value);
            this.statesSelectItem = value.map(x => ({
                label: x.State,
                value: x.State
            }));
        }
    }
    get states(): Array<IState> {
        return this._states;
    }

    private CustomersSubject = new ReplaySubject<Array<eFAACTCustomer>>(1);
    private _customers: Array<eFAACTCustomer>;
    @Input() set customers(value: Array<eFAACTCustomer>) {
        if (value && value.length > 0) {
            this._customers = value;
            this.CustomersSubject.next(value);
            this.customersNoTest = value.filter(c => c.IsTest == false);
            this.customersSelectItem = this.toCustomerSelectItems(this.customersNoTest);
            this.allCustomersSelectItem = this.toCustomerSelectItems(value);
            this.hostProvidersSelectItem = this.toCustomerSelectItems(
                value.filter(x => x.IsHostProvider == true)
            );
        }
    }
    get customers(): Array<eFAACTCustomer> {
        return this._customers;
    }

    private JobsSubject = new ReplaySubject<Array<Job>>(1);
    private _jobs: Array<Job>;
    @Input() set jobs(value: Array<Job>) {
        if (value && value.length > 0) {
            this._jobs = value;
            this.JobsSubject.next(value);
            this.jobsSelectItem = value
                .map(x => ({
                    label: x.jobtitle,
                    value: x.jobid
                }))
                .filter(j => j.value != 47);
        }
    }
    get jobs(): Array<Job> {
        return this._jobs;
    }

    private QueueJobSubject = new ReplaySubject<Array<QueueJob>>(1);
    private _queue: Array<QueueJob>;
    @Input() set queue(value: Array<QueueJob>) {
        if (value && value.length > 0) {
            this._queue = value;
            this.QueueJobSubject.next(value);

            this.queueResp = value[0].responsemessage;
            if (this.queueJobAllCustomers && this.bulkQueueTargetCount > 0) {
                this.bulkQueueCompletedCount++;
                this.percentComplete = this.calcPercentComplete(
                    this.bulkQueueCompletedCount,
                    this.bulkQueueTargetCount
                );
                if (this.bulkQueueCompletedCount >= this.bulkQueueTargetCount) {
                    this.messageService.add({
                        severity: 'info',
                        summary: 'Success',
                        detail: `Job ${value[0].jobid} queued for ${this.bulkQueueTargetCount} customers`
                    });
                    this.resetBulkQueueProgress();
                }
            } else {
                this.messageService.add({
                    severity: 'info',
                    summary: 'Success',
                    detail: value[0].responsemessage
                });
                this.progress = 0;
            }
        }
    }
    get queue(): Array<QueueJob> {
        return this._queue;
    }

    private TraceLoggingOverridesSubject = new ReplaySubject<
        Array<TraceLoggingOverride>
    >(1);
    private _traceLoggingOverrides: Array<TraceLoggingOverride>;
    @Input() set traceLoggingOverrides(value: Array<TraceLoggingOverride>) {
        this._traceLoggingOverrides = value ?? [];
        this.TraceLoggingOverridesSubject.next(this._traceLoggingOverrides);
    }
    get traceLoggingOverrides(): Array<TraceLoggingOverride> {
        return this._traceLoggingOverrides;
    }

    traceLoggingCustomerId: number;
    traceLoggingLoginName: string;
    traceLoggingMinLogLevel = 'Trace';
    traceLoggingLogTargets: string[] = [];
    traceLoggingEnabled = true;
    traceLoggingExpiresUtc: Date;
    traceLoggingNotes: string;
    editingTraceLoggingOverrideId: number;
    minLogLevelSelectItem: SelectItem[] = [
        { label: 'Trace', value: 'Trace' },
        { label: 'Debug', value: 'Debug' },
        { label: 'Info', value: 'Info' },
        { label: 'Warn', value: 'Warn' }
    ];
    logTargetSelectItem: SelectItem[] = [
        { label: 'File (f)', value: 'f' },
        { label: 'Database (db)', value: 'db' },
        { label: 'Debug (debuglog)', value: 'debuglog' }
    ];

    private UserInfoSubject = new ReplaySubject<IUserInfo>(1);
    private _userinfo: IUserInfo;
    @Input() set userInfo(value: IUserInfo) {
        if (value) {
            this._userinfo = value;
            this.UserInfoSubject.next(value);
            this.employeeName = value.EmployeeName;
        }
    }
    get userInfo(): IUserInfo {
        return this._userinfo;
    }

    private SelectedCustomerSubject = new ReplaySubject<any>(1);
    private _selectedcustomer: any;
    @Input() set selectedCustomer(value: any) {
        if (value) {
            this._selectedcustomer = value;
            this.SelectedCustomerSubject.next(value);
            this.CustomerChanged.next(value);
        }
    }
    get selectedCustomer(): any {
        return this._selectedcustomer;
    }

    private AdvisorSubject = new ReplaySubject<Array<Advisor>>(1);
    private _advisors: Array<Advisor>;
    @Input() set advisors(value: Array<Advisor>) {
        if (value && value.length > 0) {
            this._advisors = value;
            this.AdvisorSubject.next(value);
            this.advisorsSelectItem = value.map(x => ({
                label: x.AdvisorName,
                value: x.AdvisorID
            }));
        }
    }
    get advisors(): Array<Advisor> {
        return this._advisors;
    }

    advisorsSelectItem: SelectItem[];
    selectedAdvisor: any;

    customersNoTest: eFAACTCustomer[];
    testCustomers: eFAACTCustomer[];

    jobsSelectItem: SelectItem[];
    selectedJob: any;
    customersSelectItem: SelectItem[];
    allCustomersSelectItem: SelectItem[];
    hostProvidersSelectItem: SelectItem[];
    //selectedCustomer: any;
    statesSelectItem: SelectItem[];
    selectedState: any;
    dbname: any;
    serverID: any;
    serverName: any;
    agentID: any;
    customerID: any;
    customerPIN: any;
    productCode: any;
    adminCount: any;
    customerProfileID = '';
    companyName: any;
    primaryemail: any;
    private savedPrimaryEmail = '';
    address: any;
    address2: any;
    address3: any;
    city: any;
    zip: any;
    installLink: any;
    employeeName: any;
    modified: any;
    modifiedBy: any;
    hostName: any;
    hostProvider: any;
    queueJobAllCustomers = false;
    queueJobTestCustomers = false;
    storedProcedure: string;
    editingCustomer = false;
    editingLicense = false;
    editingAuthEntity = false;

    editCustomerBtnLabel = 'Edit';
    editLicenseBtnLabel = 'Edit';
    editLicenseBtnIcon = 'pi pi-pencil';
    editCustomerBtnIcon = 'pi pi-pencil';

    editAuthEntityBtnLabel = 'Edit';
    editAuthEntityBtnIcon = 'pi pi-pencil';

    editingCustomerDB = false;
    editCustomerDBBtnLabel = 'Edit';
    editCustomerDBBtnIcon = 'pi pi-pencil';

    @Output() CustomerChanged = new ReplaySubject<any>(1);
    @Output() UpdateCustomerInfo = new ReplaySubject<any>(1);
    @Output() UpdateLicenseInfo = new ReplaySubject<any>(1);
    @Output() QueueJob = new ReplaySubject<any>(1);
    @Output() UpdateAdvisor = new ReplaySubject<any>(1);
    @Output() SaveTraceLoggingOverride = new ReplaySubject<any>(1);
    @Output() DisableTraceLoggingOverride = new ReplaySubject<any>(1);

    queueResp: string;
    queueRespMulti: string[] = [];

    areYouSureDialog = false;
    showAuthorizeNetSubscriptionsDialog = false;
    showAddPurchaseDialog = false;
    addPurchaseError: string | null = null;
    newPurchase: {
        EmailAddr: string;
        PurchaseDate: Date | null;
        ProductName: string;
        ProductID: number | null;
        Subscription: string;
        SubscriptionID: string;
        PaidThruDate: Date | null;
        CancelDate: Date | null;
    } = this.createEmptyNewPurchase();
    subscriptionTypeOptions: SelectItem[] = [
        { label: 'Monthly (M)', value: 'M' },
        { label: 'Annual (A)', value: 'A' }
    ];

    includeRecycleData = false;

    maintPaidThruDate: Date;

    progress = 0;
    percentComplete = 0;
    private bulkQueueTargetCount = 0;
    private bulkQueueCompletedCount = 0;

    advisorMessage = `An authorized eFAACT Advisor is allowed access to the selected customer's eFAACT data.
     In order to open the customer's data, the eFAACT Advisor must also be setup as a QuickBooks Vendor and assigned a valid eFAACT userID and password.`;
    acknowledgedMessage = `I acknowledge the statement of understanding.`;

    microsoftDriverLink: any;
    crystalReportsLink: any;

    isGCP: boolean;
    isQBO: boolean;
    taxID: string;

    constructor(
        private messageService: MessageService,
        private authorizenetCheckoutService: AuthorizenetCheckoutService
    ) {}

    ngOnInit(): void {}

    paidThruDateChanged(rowData) {
        const i = this.items[0].Purchases.findIndex(
            x => x.ProductID == rowData.ProductID
        );
        const purchase = this.items[0].Purchases.find(
            x => x.ProductID == rowData.ProductID
        );
        purchase.MaintPaidThruDate = moment(purchase.PaidThruDate).format(
            'YYYY-MM-DD'
        );
        this.items[0].Purchases[i] = purchase;
        this.updatePurchase();
    }
    purchaseDateChanged(rowData) {
        const i = this.items[0].Purchases.findIndex(
            x => x.SubscriptionID == rowData.SubscriptionID
        );
        const purchase = this.items[0].Purchases.find(
            x => x.SubscriptionID == rowData.SubscriptionID
        );
        purchase.PurchaseDate = moment(purchase.PurchaseDate).format(
            'YYYY-MM-DD'
        );
        this.items[0].Purchases[i] = purchase;
        this.updatePurchase();
    }
    productNameChanged(rowData) {
        const i = this.items[0].Purchases.findIndex(
            x => x.SubscriptionID == rowData.SubscriptionID
        );
        const purchase = this.items[0].Purchases.find(
            x => x.SubscriptionID == rowData.SubscriptionID
        );
        purchase.ProductName = rowData.ProductName;
        this.items[0].Purchases[i] = purchase;
        this.updatePurchase();
    }
    productIDChanged(rowData) {
        const i = this.items[0].Purchases.findIndex(
            x => x.SubscriptionID == rowData.SubscriptionID
        );
        const purchase = this.items[0].Purchases.find(
            x => x.SubscriptionID == rowData.SubscriptionID
        );       
        purchase.ProductID = rowData.ProductID;
        this.items[0].Purchases[i] = purchase;
        this.updatePurchase();
    }
    subscriptionTypeChanged(rowData) {
        const i = this.items[0].Purchases.findIndex(
            x => x.SubscriptionID == rowData.SubscriptionID
        );
        const purchase = this.items[0].Purchases.find(
            x => x.SubscriptionID == rowData.SubscriptionID
        );
        purchase.ProductID = rowData.ProductID;
        this.items[0].Purchases[i] = purchase;
        this.updatePurchase();
    }
    subscriptionIDChanged(rowData) {
        const i = this.items[0].Purchases.findIndex(
            x => x.SubscriptionID == rowData.SubscriptionID
        );
        const purchase = this.items[0].Purchases.find(
            x => x.SubscriptionID == rowData.SubscriptionID
        );
        purchase.ProductID = rowData.ProductID;
        this.items[0].Purchases[i] = purchase;
        this.updatePurchase();
    }

    customerChanged(event) {
        // this.selectedCustomer = event.value;
        // if (this.selectedCustomer) {
        //     this.CustomerChanged.next(this.selectedCustomer);
        // }
    }
    editAuthEntity() {
        this.editingAuthEntity = !this.editingAuthEntity;
        this.editAuthEntityBtnLabel = this.editingAuthEntity ? 'Lock' : 'Edit';
        this.editAuthEntityBtnIcon = this.editingAuthEntity
            ? 'pi pi-lock'
            : 'pi pi-pencil';
    }
    editLicenseInfo() {
        this.editingLicense = !this.editingLicense;
        this.editLicenseBtnLabel = this.editingLicense ? 'Lock' : 'Edit';
        this.editLicenseBtnIcon = this.editingLicense
            ? 'pi pi-lock'
            : 'pi pi-pencil';
    }
    editCustomerInfo() {
        this.editingCustomer = !this.editingCustomer;
        this.editCustomerBtnLabel = this.editingCustomer ? 'Lock' : 'Edit';
        this.editCustomerBtnIcon = this.editingCustomer
            ? 'pi pi-lock'
            : 'pi pi-pencil';
    }
    editCustomerDB() {
        this.editingCustomerDB = !this.editingCustomerDB;
        this.editCustomerDBBtnLabel = this.editingCustomerDB ? 'Lock' : 'Edit';
        this.editCustomerDBBtnIcon = this.editingCustomerDB
            ? 'pi pi-lock'
            : 'pi pi-pencil';
    }

    updateAdvisor() {
        const data = JSON.stringify({
            CustomerID: this.customerID,
            Name: this.companyName,
            primaryemail: this.primaryemail,
            PIN: this.customerPIN,
            Address1: this.address,
            Address2: this.address2,
            Address3: this.address3,
            City: this.city,
            State: this.selectedState,
            ZipCode: this.zip,
            AdvisorName: '',
            AdvisorID: this.selectedAdvisor,
            ModifiedBy: this.employeeName,
            IsAdminUpdate: false,
            IsGCP: this.isGCP,
            IsQBO: this.isQBO,
            TaxpayerID: this.taxID,
            HostProviderID: this.hostProvider,
            CustomerProfileID: this.customerProfileID?.trim() || null,
            IsInternal: true
        });

        this.UpdateAdvisor.next(data);
        this.editingAuthEntity = !this.editingAuthEntity;
        this.editAuthEntityBtnLabel = this.editingAuthEntity ? 'Lock' : 'Edit';
        this.editAuthEntityBtnIcon = this.editingAuthEntity
            ? 'pi pi-lock'
            : 'pi pi-pencil';
    }

    updateLicenseInfo() {
        if (!this.items?.[0]) {
            return;
        }

        const adminCount =
            this.adminCount != null && this.adminCount !== ''
                ? Number(this.adminCount)
                : this.items[0].AdminCount;
        const customerProfileId = this.customerProfileID?.trim();

        const data = JSON.stringify({
            ...this.items[0],
            CustomerID: this.customerID,
            PIN: this.customerPIN,
            ProductCode: this.productCode,
            AdminCount: adminCount,
            CustomerProfileID: customerProfileId || null,
            dbname: this.dbname,
            ServerID: this.serverID,
            ServerName: this.serverName,
            AgentID: this.agentID,
            ModifiedBy: this.employeeName,
            eFCModifiedBy: this.employeeName,
            IsInternal: true
        });
        this.UpdateLicenseInfo.next(data);
        this.editingLicense = !this.editingLicense;
        this.editLicenseBtnLabel = this.editingLicense ? 'Lock' : 'Edit';
        this.editLicenseBtnIcon = this.editingLicense
            ? 'pi pi-lock'
            : 'pi pi-pencil';
    }
    updateCustomerInfo() {
        const data = JSON.stringify({
            CustomerID: this.customerID,
            Name: this.companyName,
            primaryemail: this.primaryemail,
            PIN: this.customerPIN,
            Address1: this.address,
            Address2: this.address2,
            Address3: this.address3,
            City: this.city,
            State: this.selectedState,
            ZipCode: this.zip,
            AdvisorName: '',
            AdvisorID: this.selectedAdvisor,
            ModifiedBy: this.employeeName,
            IsAdminUpdate: false,
            IsGCP: this.isGCP,
            IsQBO: this.isQBO,
            TaxpayerID: this.taxID,
            //HostName: this.hostName,
            HostProviderID: this.hostProvider,
            CustomerProfileID: this.customerProfileID?.trim() || null,
            IsInternal: true
        });

        const primaryEmailChanged = this.primaryEmailChanged();
        this.UpdateCustomerInfo.next(data);
        if (primaryEmailChanged) {
            this.updateAuthorizeNetProfileEmail();
        }
        this.editingCustomer = !this.editingCustomer;
        this.editCustomerBtnLabel = this.editingCustomer ? 'Lock' : 'Edit';
        this.editCustomerBtnIcon = this.editingCustomer
            ? 'pi pi-lock'
            : 'pi pi-pencil';
    }

    private primaryEmailChanged(): boolean {
        const nextEmail = (this.primaryemail ?? '').trim();
        const previousEmail = (this.savedPrimaryEmail ?? '').trim();
        return (
            nextEmail.length > 0 &&
            nextEmail.toLowerCase() !== previousEmail.toLowerCase()
        );
    }

    private updateAuthorizeNetProfileEmail() {
        const profileId = this.customerProfileID?.trim();
        const nextEmail = (this.primaryemail ?? '').trim();
        if (!profileId || !this.primaryEmailChanged()) {
            return;
        }

        this.authorizenetCheckoutService
            .updateCustomerProfileEmail(profileId, nextEmail)
            .subscribe({
                next: () => {
                    this.savedPrimaryEmail = nextEmail;
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Authorize.net',
                        detail: 'Customer profile email updated.'
                    });
                },
                error: (err) => {
                    const detail =
                        err?.error?.Message ||
                        err?.error?.messages?.message?.[0]?.text ||
                        'Company info was saved, but the Authorize.net customer profile email was not updated.';
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Authorize.net',
                        detail
                    });
                }
            });
    }

    updateCustomerDB() {
        const data = JSON.stringify({
            CustomerID: this.customerID,
            Name: this.companyName,
            primaryemail: this.primaryemail,
            PIN: this.customerPIN,
            Address1: this.address,
            Address2: this.address2,
            Address3: this.address3,
            City: this.city,
            State: this.selectedState,
            ZipCode: this.zip,
            AdvisorName: '',
            AdvisorID: this.selectedAdvisor,
            ModifiedBy: this.employeeName,
            IsAdminUpdate: false,
            IsGCP: this.isGCP,
            IsQBO: this.isQBO,
            TaxpayerID: this.taxID,
            HostProviderID: this.hostProvider,
            CustomerProfileID: this.customerProfileID?.trim() || null,
            IsInternal: true
        });

        this.UpdateCustomerInfo.next(data);
        this.editingCustomerDB = !this.editingCustomerDB;
        this.editCustomerDBBtnLabel = this.editingCustomerDB ? 'Lock' : 'Edit';
        this.editCustomerDBBtnIcon = this.editingCustomerDB
            ? 'pi pi-lock'
            : 'pi pi-pencil';
    }

    updatePurchase() {
        this.items[0] = {...this.items[0], IsAdminUpdate: true, EmailAddr: this.newPurchase.EmailAddr};
        const data = JSON.stringify(this.items[0]);

        this.UpdateCustomerInfo.next(data);
    }

    private createEmptyNewPurchase() {
        return {
            EmailAddr: '',
            PurchaseDate: new Date(),
            ProductName: '',
            ProductID: null as number | null,
            Subscription: 'M',
            SubscriptionID: '',
            PaidThruDate: null as Date | null,
            CancelDate: null as Date | null
        };
    }

    openAddPurchaseDialog(): void {
        if (!this.items?.[0]) {
            return;
        }
        this.newPurchase = this.createEmptyNewPurchase();
        this.addPurchaseError = null;
        this.showAddPurchaseDialog = true;
    }

    cancelAddPurchase(): void {
        this.showAddPurchaseDialog = false;
        this.addPurchaseError = null;
        this.newPurchase = this.createEmptyNewPurchase();
    }

    addPurchase(): void {
        if (!this.items?.[0]) {
            return;
        }

        const productName = this.newPurchase.ProductName?.trim() ?? '';
        const productId = Number(this.newPurchase.ProductID);
        const subscriptionId = this.newPurchase.SubscriptionID?.trim() ?? '';
        const subscriptionType = (
            this.newPurchase.Subscription ?? ''
        )
            .trim()
            .toUpperCase();

        if (!productName) {
            this.addPurchaseError = 'Product Name is required.';
            return;
        }
        if (!Number.isFinite(productId) || productId <= 0) {
            this.addPurchaseError = 'Product ID is required.';
            return;
        }
        if (!this.newPurchase.PurchaseDate) {
            this.addPurchaseError = 'Purchase Date is required.';
            return;
        }

        const purchaseDate = moment(this.newPurchase.PurchaseDate).format(
            'YYYY-MM-DD'
        );
        const maintPaidThruDate = this.newPurchase.PaidThruDate
            ? moment(this.newPurchase.PaidThruDate).format('YYYY-MM-DD')
            : undefined;
        const cancelDate = this.newPurchase.CancelDate
            ? moment(this.newPurchase.CancelDate).format('YYYY-MM-DD')
            : undefined;

        const purchase: Purchase = {
            SubscriptionID: subscriptionId || undefined,
            ProductID: productId,
            ProductName: productName,
            ProductDesc: productName,
            PurchaseDate: purchaseDate,
            MaintPaidDate: purchaseDate,
            MaintPaidThruDate: maintPaidThruDate ?? null,
            PaidThruDate: maintPaidThruDate ?? null,
            Subscription: subscriptionType || undefined,
            Quantity: 1,
            EID: 0,
            IsCancel: !!cancelDate,
            CancelDate: cancelDate,
            OrderNumber: null,
            CouponCode: null,
            LicensingAcceptedBy: null,
            LicensingAcceptedDate: null,
            Licenses: null
        };

        if (!this.items[0].Purchases) {
            this.items[0].Purchases = [];
        }
        this.items[0].Purchases = [...this.items[0].Purchases, purchase];
        this.updatePurchase();
        this.cancelAddPurchase();
    }

    queueForAllChanged(event) {
        if (event.checked == true) {
            this.selectedCustomer = null;
        }
    }

    queueForTestChanged(event) {
        if (event.checked == true) {
            this.selectedCustomer = null;
            this.testCustomers = this.customers.filter(c => c.IsTest == true);
            this.customersSelectItem = this.toCustomerSelectItems(this.testCustomers);
        } else {
            this.customersSelectItem = this.toCustomerSelectItems(this.customersNoTest);
        }
    }

    addRecycleDataToJobList(event) {
        //this.queueJobAllCustomers = false;
        if (event.checked == true) {
            this.jobsSelectItem = this.jobs.map(x => ({
                label: x.jobtitle,
                value: x.jobid
            }));
        } else {
            this.jobsSelectItem = this.jobs
                .map(x => ({
                    label: x.jobtitle,
                    value: x.jobid
                }))
                .filter(j => j.value != 47);
        }
    }

    queueJob() {
        this.percentComplete = 0;
        if (this.selectedJob == 47) {
            this.areYouSureDialog = true;
        } else {
            if (this.queueJobAllCustomers == true) {
                this.startBulkQueue(this.customersSelectItem);
            } else {
                this.progress = this.customers.length - 1;
                this.percentComplete = this.calcPercentComplete(this.progress);
                this.QueueJob.next({
                    employeelistid: null,
                    customerid: this.selectedCustomer,
                    jobid: this.selectedJob,
                    storedproc:
                        this.storedProcedure != null
                            ? this.storedProcedure
                            : null
                });
            }
        }
    }

    calcPercentComplete(completed: number, total?: number): number {
        const denominator =
            total ??
            (this.queueJobTestCustomers
                ? this.testCustomers?.length ?? 0
                : (this.customersNoTest?.length ?? 1) - 1);
        if (!denominator) {
            return 0;
        }
        return Math.round((completed / denominator) * 100);
    }

    private toCustomerSelectItems(customers: eFAACTCustomer[]): SelectItem[] {
        return [...customers]
            .sort((a, b) => a.custid - b.custid)
            .map(x => ({
                label: x.custname,
                value: x.custid
            }));
    }

    private startBulkQueue(customers: SelectItem[]): void {
        this.bulkQueueTargetCount = customers.length;
        this.bulkQueueCompletedCount = 0;
        this.progress = 0;
        this.percentComplete = 0;
        customers.forEach((customer, index) => {
            this.queueJobsForAllCustomers(customer, index + 1);
        });
    }

    private resetBulkQueueProgress(): void {
        this.bulkQueueTargetCount = 0;
        this.bulkQueueCompletedCount = 0;
        this.progress = 0;
        this.percentComplete = 0;
    }

    queueJobsForAllCustomers(customer, progress) {
        setTimeout(() => {
            if (this.bulkQueueTargetCount === 0) {
                this.percentComplete = this.calcPercentComplete(progress);
            }
            this.QueueJob.next({
                employeelistid: null,
                customerid: customer.value,
                jobid: this.selectedJob,
                storedproc:
                    this.storedProcedure != null ? this.storedProcedure : null
            });
        }, 0);

    }

    queueJob47() {
        this.areYouSureDialog = false;
        if (this.queueJobAllCustomers == true) {
            this.startBulkQueue(this.customersSelectItem);
        } else {
            this.QueueJob.next({
                employeelistid: null,
                customerid: this.selectedCustomer,
                jobid: this.selectedJob,
                storedproc:
                    this.storedProcedure != null ? this.storedProcedure : null
            });
        }
    }

    clearQueueJobsForm() {
        this.storedProcedure = null;
        this.selectedJob = null;
        this.selectedCustomer = null;
        this.queueJobAllCustomers = false;
        this.queueJobTestCustomers = false;
        this.includeRecycleData = false;
        this.percentComplete = 0;
        this.progress = 0;
        this.resetBulkQueueProgress();

        this.jobsSelectItem = this.jobs
            .map(x => ({
                label: x.jobtitle,
                value: x.jobid
            }))
            .filter(j => j.value != 47);
    }

    isGCPChanged(event) {

    }
    isQBOChanged(event) {

    }

    editTraceLoggingOverride(row: TraceLoggingOverride) {
        this.editingTraceLoggingOverrideId = row.TraceLoggingOverrideId;
        this.traceLoggingCustomerId = row.CustomerId;
        this.traceLoggingLoginName = row.LoginName;
        this.traceLoggingMinLogLevel = row.MinLogLevel ?? 'Trace';
        this.traceLoggingLogTargets = row.LogTargets
            ? row.LogTargets.split(',').map(x => x.trim()).filter(x => x)
            : [];
        this.traceLoggingEnabled = row.Enabled;
        this.traceLoggingExpiresUtc = row.ExpiresUtc
            ? new Date(row.ExpiresUtc)
            : null;
        this.traceLoggingNotes = row.Notes;
    }

    clearTraceLoggingForm() {
        this.editingTraceLoggingOverrideId = null;
        this.traceLoggingCustomerId = null;
        this.traceLoggingLoginName = null;
        this.traceLoggingMinLogLevel = 'Trace';
        this.traceLoggingLogTargets = [];
        this.traceLoggingEnabled = true;
        this.traceLoggingExpiresUtc = null;
        this.traceLoggingNotes = null;
    }

    saveTraceLoggingOverride() {
        if (!this.traceLoggingCustomerId && !this.traceLoggingLoginName) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Validation',
                detail: 'Customer ID or Login Name is required.'
            });
            return;
        }

        const payload = {
            traceLoggingOverrideId: this.editingTraceLoggingOverrideId,
            customerId: this.traceLoggingCustomerId ?? undefined,
            loginName: this.traceLoggingLoginName || undefined,
            minLogLevel: this.traceLoggingMinLogLevel,
            logTargets:
                this.traceLoggingLogTargets.length > 0
                    ? this.traceLoggingLogTargets.join(',')
                    : undefined,
            enabled: this.traceLoggingEnabled,
            expiresUtc: this.traceLoggingExpiresUtc
                ? this.traceLoggingExpiresUtc.toISOString()
                : undefined,
            notes: this.traceLoggingNotes || undefined
        };

        this.SaveTraceLoggingOverride.next(payload);
        this.messageService.add({
            severity: 'success',
            summary: 'Saved',
            detail: 'Trace logging override saved.'
        });
        this.clearTraceLoggingForm();
    }

    disableTraceLoggingOverride(row: TraceLoggingOverride) {
        this.DisableTraceLoggingOverride.next({
            traceLoggingOverrideId: row.TraceLoggingOverrideId
        });
        this.messageService.add({
            severity: 'info',
            summary: 'Disabled',
            detail: `Override ${row.TraceLoggingOverrideId} disabled.`
        });
    }
}
